<script setup lang="ts">
import { computed, ref } from "vue";
import { Project } from "@/store/search";
import { useRoute } from "vue-router";
import ProjectListItemParameter from "@/components/main/search-panel/ProjectListItemParameter.vue";

type ProjectItemProps = {
	project: Project
}

const route = useRoute()
const props = defineProps<ProjectItemProps>()

const isExpanded = ref(false)

const isOpened = computed(() =>
	route.query.page === 'project' && Number(route.query.id) === props.project.Id
)

function formatDate(date?: string | Date) {
	if (!date) return "";

	const dateObj = typeof date === "string" ? new Date(date) : date;
	return dateObj.toLocaleDateString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "2-digit",
	});
}
</script>

<template>
	<div
		class="projects-list__body-item"
		:class="{ 'projects-list__body-item--opened': isOpened }"
		:data-project-id="props.project.Id"
	>
		<div class="projects-list__body-item-colored" :style="{ backgroundColor: props.project.Color }"/>
		<router-link
			:to="{ query: { page: 'project', id: props.project.Id } }"
			class="projects-list__body-item-name"
			v-html="props.project.Name"
		/>
		<div
			v-if="props.project.Clients?.length"
			class="projects-list__body-item-clients"
		>
			<template
				v-for="(c, index) in props.project.Clients.slice(0, 2)"
				:key="c.Id"
			>
				<router-link
					:to="{ query: { page: 'client', id: c.Id } }"
					class="projects-list__body-item-client"
				>
					<span v-html="c.Name"/>
				</router-link>
				<span
					v-if="index === 0 ? props.project.Clients.length > 1 : isExpanded"
					class="projects-list__body-item-client-divider"
				>&nbsp;/ </span>
			</template>
			<span
				v-if="!isExpanded && props.project.Clients.length > 2"
				class="projects-list__body-item-client-show-more"
				@click="isExpanded = true"
			>
				еще {{ props.project.Clients.length - 2 }}
			</span>
			<template v-if="isExpanded">
				<template
					v-for="(c, index) in props.project.Clients.slice(2)"
					:key="c.Id"
				>
					<router-link
						:to="{ query: { page: 'client', id: c.Id } }"
						class="projects-list__body-item-client"
					>
						<span v-html="c.Name"/>
					</router-link>
					<span
						v-if="index + 1 < props.project.Clients.slice(2).length"
						class="projects-list__body-item-client-divider"
					>&nbsp;/ </span>
				</template>
			</template>
		</div>
		<div
			v-if="props.project.Parameters?.length"
			class="projects-list__body-item-parameters"
		>
			<ProjectListItemParameter
				v-for="(param, index) in props.project.Parameters"
				:key="index"
				:parameter="param"
			/>
		</div>
		<div class="projects-list__body-item-footer">
			<div class="projects-list__body-item-tags">
				<span
					v-for="tg in props.project.Tags"
					:key="tg.Id"
					class="projects-list__body-item-tag"
					v-html="`#${tg.Name.toLowerCase()}`"
				/>
			</div>
			<div class="projects-list__body-item-date">
				{{ formatDate(props.project.CreateTime) }}
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.projects-list__body-item {
	display: flex;
	flex-direction: column;
	position: relative;
	border-left: 4px solid transparent;
	border-radius: 8px;
	padding: 4px 4px 0 8px;

	&--opened {
		background-color: #F3F8F4;
		border-top-right-radius: 8px;
		border-bottom-right-radius: 8px;
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
		animation: highlight-blink 2s ease-in-out;
	}

	&-colored {
		position: absolute;
		left: 0;
		top: 0;
		bottom: 0;
		width: 4px;
		border-top-left-radius: 8px;
		border-bottom-left-radius: 8px;
	}

	&-name {
		color: #438451;
		font-size: 14px;
		line-height: 18px;
		font-weight: 600;
		margin-bottom: 4px;
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}

	&-clients {
		font-size: 12px;
		line-height: 16px;
		display: inline;
		margin-bottom: 4px;
	}

	&-client {
		color: #438451;
		text-decoration: none;
		font-size: 14px;
		line-height: 18px;

		&:hover {
			text-decoration: underline;
		}

		&-divider {
			color: #4D4D4D;
			font-weight: 500;
		}

		&-show-more {
			background-color: #F2F2F2;
			font-size: 12px;
			line-height: 16px;
			padding: 2px 4px;
			border-radius: 8px;
			cursor: pointer;
			color: #979797;
			margin-left: 4px;

			&:hover {
				color: #4D4D4D;
			}
		}
	}

	&-parameters {
		display: flex;
		flex-direction: column;
		margin-bottom: 4px;
	}

	&-footer {
		display: flex;
		justify-content: space-between;
	}

	&-tags {
		display: flex;
		flex-wrap: wrap;
	}

	&-tag {
		font-size: 14px;
		line-height: 18px;
		color: #979797;
	}

	&-date {
		font-size: 14px;
		line-height: 18px;
		color: #979797;
		text-align: right;
	}
}

@keyframes highlight-blink {
	0%    { background-color: #F3F8F4; }
	16.6% { background-color: transparent; }
	33.3% { background-color: #F3F8F4; }
	50%   { background-color: transparent; }
	66.6% { background-color: #F3F8F4; }
	83.3% { background-color: transparent; }
	100%  { background-color: #F3F8F4; }
}
</style>
